import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './sessionsTab.module.scss';
import { useProfile } from 'hooks/useProfile';
import { LoginHistoryItem } from 'types/loginHistory';
import { Pagination } from 'components/pagination';
import DivTableSkeleton, { DivTableColumn } from 'components/skeleton/DivTableSkeleton';

const SessionsTab: React.FC = () => {
  const { t } = useTranslation();
  const { fetchLoginHistory } = useProfile();
  const [sessions, setSessions] = useState<LoginHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const tableColumns: DivTableColumn[] = useMemo(
    () => [
      { label: t('settings.sessions.columns.browser'), width: '80%' },
      { label: t('settings.sessions.columns.location'), width: '60%' },
      { label: t('settings.sessions.columns.ipAddress'), width: '70%' },
      { label: t('settings.sessions.columns.lastSeen'), width: '90%' },
    ],
    [t],
  );

  useEffect(() => {
    const loadSegments = async () => {
      setIsLoading(true);
      const result = await fetchLoginHistory(page, 10, 'id', 'DESC', false);
      if (result && Array.isArray(result.docs)) {
        setSessions(result.docs);
        setTotalPages(result.totalPages || 1);
      }
      setIsLoading(false);
    };
    loadSegments();
  }, [page]);

  return (
    <div className={styles.sessionsTab}>
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          {tableColumns.map((col) => (
            <div key={col.label} className={styles.th}>
              {col.label}
            </div>
          ))}
        </div>

        <div className={styles.tableBody}>
          {isLoading ? (
            <DivTableSkeleton
              rows={5}
              rowClassName={styles.tableRow}
              cellClassName={styles.td}
              labelClassName={styles.mobileLabel}
              columns={tableColumns}
            />
          ) : sessions.length === 0 ? (
            <div className={styles.tableRow} style={{ justifyContent: 'center' }}>
              <div className={styles.td}>{t('settings.sessions.empty')}</div>
            </div>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className={styles.tableRow}>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('settings.sessions.columns.browser')}</span>
                  <span className={styles.browserText}> {session.userAgent || t('settings.sessions.unknown')}</span>
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('settings.sessions.columns.location')}</span>
                  {session?.location ?? t('settings.sessions.unknown')}
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('settings.sessions.columns.ipAddress')}</span>
                  {session.ipAddress || t('settings.sessions.unknown')}
                </div>
                <div className={styles.td}>
                  <span className={styles.mobileLabel}>{t('settings.sessions.columns.lastSeen')}</span>
                  <span>{new Date(session.updatedAt).toLocaleString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
};

export default SessionsTab;
